import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '../../../utils/useMediaQuery';
import { getMakecodeArcadePageRootStyle } from '../makecodeArcadePageStyles';
import { toast } from 'react-toastify';
import Button from '../../../components/ui/Button';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import CodeIcon from '../../../components/icons/CodeIcon';

// 이미지 변환 확장 기능 컴포넌트
const ImageConverter: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [jsCode, setJsCode] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sizeOption, setSizeOption] = useState<string>('original');
  const [customWidth, setCustomWidth] = useState<number>(16);
  const [customHeight, setCustomHeight] = useState<number>(16);
  const [convertedImageUrl, setConvertedImageUrl] = useState<string | null>(null);
  const [codeType, setCodeType] = useState<'sprite' | 'background'>('sprite');
  
  const navigate = useNavigate();
  /** Layout과 동일: window.innerWidth < 768 (모바일) */
  const isMobile = useMediaQuery('(max-width: 767px)');

  // MakeCode Arcade 팔레트 (0: 투명, 1~15: 색상)
  const palette = [
    [0, 0, 0, 0],        // 0: 투명 (RGBA)
    [255, 255, 255],     // 1: White
    [255, 33, 33],       // 2: Red
    [255, 147, 196],     // 3: Pink
    [255, 129, 53],      // 4: Orange
    [255, 246, 9],       // 5: Yellow
    [36, 156, 163],      // 6: Teal
    [120, 220, 82],      // 7: Green
    [0, 63, 173],        // 8: Blue
    [135, 242, 255],     // 9: Light Blue
    [142, 46, 196],      // 10: Purple
    [164, 131, 159],     // 11: Light Purple
    [92, 64, 108],       // 12: Dark Purple
    [229, 205, 196],     // 13: Light Brown
    [145, 70, 61],       // 14: Brown
    [0, 0, 0]            // 15: Black
  ];

  // RGB 거리 기반 팔레트 색상 매칭
  const findClosestColor = (r: number, g: number, b: number): number => {
    let minDist = Infinity;
    let idx = 1;
    for (let i = 1; i < palette.length; i++) {
      const [pr, pg, pb] = palette[i];
      const dist = Math.pow(r - pr, 2) + Math.pow(g - pg, 2) + Math.pow(b - pb, 2);
      if (dist < minDist) {
        minDist = dist;
        idx = i;
      }
    }
    return idx;
  };

  // 이미지 선택 처리
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setError(null);
      
      // 이미지 미리보기 생성
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // 이미지 크기 계산 함수
  const calculateImageSize = (img: HTMLImageElement): { width: number; height: number } => {
    let width = img.width;
    let height = img.height;

    switch (sizeOption) {
      case 'original':
        return { width, height };
      case 'width':
        // 너비를 160으로 맞추고 비율 유지
        height = Math.round((height * 160) / width);
        width = 160;
        return { width, height };
      case 'height':
        // 높이를 120으로 맞추고 비율 유지
        width = Math.round((width * 120) / height);
        height = 120;
        return { width, height };
      case 'simulator':
        // 시뮬레이터 크기(160x120)에 맞추기
        return { width: 160, height: 120 };
      case 'custom':
        return { width: customWidth, height: customHeight };
      default:
        return { width, height };
    }
  };

  // 이미지 변환 처리
  const handleConvert = () => {
    if (!selectedImage) return;

    setLoading(true);
    setError(null);

    const img = new window.Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const { width, height } = calculateImageSize(img);
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Canvas context를 가져올 수 없습니다.');
        }

        // 이미지 그리기
        ctx.drawImage(img, 0, 0, width, height);
        
        // 픽셀 데이터 추출
        const imageData = ctx.getImageData(0, 0, width, height);
        const pixels = imageData.data;
        
        // 변환된 이미지 미리보기 생성
        setConvertedImageUrl(canvas.toDataURL());

        // MakeCode Arcade 코드 생성
        let code = '';
        
        if (codeType === 'sprite') {
          code = `// 스프라이트 이미지 생성\n`;
          code += `let mySprite = sprites.create(img\`\n`;
        } else {
          code = `// 배경 이미지 설정\n`;
          code += `scene.setBackgroundImage(img\`\n`;
        }

        // 픽셀 데이터를 MakeCode Arcade 형식으로 변환
        for (let y = 0; y < height; y++) {
          let row = '';
          for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const r = pixels[idx];
            const g = pixels[idx + 1];
            const b = pixels[idx + 2];
            const a = pixels[idx + 3];
            
            if (a < 128) {
              // 투명한 픽셀
              row += '.';
            } else {
              // 색상 매칭
              const colorIndex = findClosestColor(r, g, b);
              row += colorIndex.toString(16);
            }
          }
          code += row + '\n';
        }
        
        code += '`)\n';
        
        if (codeType === 'sprite') {
          code += `\n// 스프라이트 위치 설정\nmySprite.setPosition(80, 60)`;
        }

        setJsCode(code);
      } catch (err) {
        setError('이미지 변환 중 오류가 발생했습니다: ' + (err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    img.onerror = () => {
      setError('이미지를 로드할 수 없습니다.');
      setLoading(false);
    };

    img.src = URL.createObjectURL(selectedImage);
  };

  // 코드 복사 처리
  const handleCopyCode = () => {
    if (jsCode) {
      navigator.clipboard.writeText(jsCode);
      toast.success('코드가 클립보드에 복사되었습니다!');
    }
  };

  // 파일 선택 다이얼로그 열기
  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
        <Button variant="line" leftIcon={<LeftArrowIcon width={20} height={20} />} onClick={() => navigate(-1)} style={{ marginRight: '16px' }}>
          돌아가기
        </Button>
        <h1 style={{ margin: 0, fontSize: '24px', fontFamily: 'var(--font-family-primary)' }}>이미지 변환기</h1>
      </div>

      <div style={{ padding: '24px', marginBottom: '24px', backgroundColor: 'var(--Color-background-normal)', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '20px' }}>
          이미지 변환기
        </h2>
        <p style={{ marginBottom: '16px', color: 'var(--Color-text-normal)' }}>
          이미지를 선택하면 MakeCode Arcade에서 사용할 수 있는 자바스크립트 코드로 변환합니다.
        </p>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '8px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          
          <Button variant="primary" onClick={handleOpenFileDialog} style={{ width: '100%' }}>
            이미지 선택
          </Button>

          <fieldset style={{ marginTop: '16px', border: '1px solid var(--Color-line-normal)', borderRadius: '8px', padding: '16px' }}>
            <legend style={{ fontWeight: 600, marginBottom: '8px' }}>코드 생성 방식</legend>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="radio" name="codeType" value="sprite" checked={codeType === 'sprite'} onChange={e => setCodeType(e.target.value as 'sprite' | 'background')} />
                스프라이트로 추가
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="radio" name="codeType" value="background" checked={codeType === 'background'} onChange={e => setCodeType(e.target.value as 'sprite' | 'background')} />
                배경으로 추가
              </label>
            </div>
          </fieldset>
          
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>이미지 크기 설정</label>
            <select
              value={sizeOption}
              onChange={(e) => setSizeOption(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--Color-line-normal)', fontSize: '14px' }}
            >
              <option value="original">원본 크기</option>
              <option value="width">시뮬레이터 너비에 맞추기 (160px)</option>
              <option value="height">시뮬레이터 높이에 맞추기 (120px)</option>
              <option value="simulator">시뮬레이터에 맞추기 (160px*120px)</option>
              <option value="custom">사용자 지정 크기</option>
            </select>
          </div>

          {sizeOption === 'custom' && (
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>너비</label>
                <input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Number(e.target.value))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--Color-line-normal)' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>높이</label>
                <input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Number(e.target.value))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--Color-line-normal)' }}
                />
              </div>
            </div>
          )}

          {imagePreview && (
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', gap: '32px', margin: '16px 0' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '8px', fontWeight: 500 }}>원본 이미지</div>
                <img
                  src={imagePreview}
                  alt="원본 미리보기"
                  style={{ maxWidth: 200, maxHeight: 200, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </div>
              {convertedImageUrl && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '8px', fontWeight: 500 }}>변환된 이미지</div>
                  <img
                    src={convertedImageUrl}
                    alt="변환 미리보기"
                    style={{ maxWidth: 200, maxHeight: 200, border: '1px solid #ccc', borderRadius: 8 }}
                  />
                </div>
              )}
            </div>
          )}

          <Button
            variant="primary"
            onClick={handleConvert}
            disabled={!selectedImage || loading}
            leftIcon={!loading ? <CodeIcon width={20} height={20} /> : undefined}
            style={{ width: '100%' }}
          >
            {loading ? '변환 중...' : '이미지 변환하기'}
          </Button>
        </div>

        {jsCode && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>생성된 코드:</h3>
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                maxHeight: 300,
                overflow: 'auto',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontSize: '13px',
              }}
            >
              {jsCode}
            </div>
            <Button variant="secondary" onClick={handleCopyCode} style={{ marginTop: '16px', width: '100%' }}>
              코드 복사하기
            </Button>
          </div>
        )}
      </div>

      <div style={{ padding: '24px', backgroundColor: 'var(--Color-background-normal)', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>사용 방법:</h3>
        <p style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
          1. "이미지 선택" 버튼을 클릭하여 변환할 이미지를 선택합니다.
        </p>
        <p style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
          2. 이미지 크기 설정을 선택합니다:
        </p>
        <ul style={{ marginBottom: '12px', paddingLeft: '24px', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
          <li>원본 크기: 이미지의 원래 크기를 유지합니다.</li>
          <li>시뮬레이터 너비에 맞추기: 너비를 160px로 맞추고 비율을 유지합니다.</li>
          <li>시뮬레이터 높이에 맞추기: 높이를 120px로 맞추고 비율을 유지합니다.</li>
          <li>시뮬레이터에 맞추기: 160px*120px 크기에 맞추기</li>
          <li>사용자 지정 크기: 원하는 크기를 직접 입력합니다.</li>
        </ul>
        <p style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
          3. "이미지 변환하기" 버튼을 클릭하여 자바스크립트 코드를 생성합니다.
        </p>
        <p style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
          4. 생성된 코드를 복사하여 MakeCode Arcade의 자바스크립트 편집기에 붙여넣습니다.
        </p>
        <p style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>
          5. 코드를 실행하면 선택한 이미지가 게임 화면에 표시됩니다.
        </p>
      </div>
    </div>
  );
};

export default ImageConverter; 